import { useCallback, useMemo, useRef, useState } from 'react';

import { OutreachOrigin } from '@/shared/lib/outreach-origin';

import type { GenerateReachoutFromIdsRequestDto, GenerateReachoutFromIdsResponseDto } from '../model';
import { generateAiReachoutMessageClient } from './client';

export type BulkSlotStatus = 'pending' | 'loading' | 'success' | 'error';

export type BulkSlot = {
  esPersonId: string;
  candidateName: string;
  status: BulkSlotStatus;
  data: GenerateReachoutFromIdsResponseDto | null;
  error: Error | null;
};

export type BulkProgress = {
  total: number;
  completed: number;
  failed: number;
  pending: number;
};

export type BulkGenerateItem = GenerateReachoutFromIdsRequestDto & {
  candidateName: string;
  whatsappTemplateMessage?: string;
};

const EMPTY_MAP = new Map<string, BulkSlot>();

export function useBulkGenerateReachoutMessages(concurrency = 5) {
  const [slots, setSlots] = useState<Map<string, BulkSlot>>(EMPTY_MAP);
  const queueRef = useRef<BulkGenerateItem[]>([]);
  const isRunningRef = useRef(false);

  const updateSlot = useCallback((esPersonId: string, patch: Partial<BulkSlot>) => {
    setSlots((prev) => {
      const next = new Map(prev);
      const current = next.get(esPersonId);
      if (current) {
        next.set(esPersonId, { ...current, ...patch });
      }
      return next;
    });
  }, []);

  const regenerateSlot = useCallback(
    async (
      esPersonId: string,
      req: Pick<GenerateReachoutFromIdsRequestDto, 'job_improved_id' | 'outreach_type' | 'language'> & {
        whatsappTemplateMessage?: string;
      },
    ) => {
      if (req.outreach_type === 'whatsapp') {
        return;
      }

      updateSlot(esPersonId, { status: 'loading', error: null });
      try {
        const { data } = await generateAiReachoutMessageClient.generateReachoutMessageFromIds({
          es_person_id: esPersonId,
          job_improved_id: req.job_improved_id,
          outreach_type: req.outreach_type,
          language: req.language ?? 'nl',
          origin: OutreachOrigin.rjCommunicationBulk,
        });
        updateSlot(esPersonId, { status: 'success', data });
      } catch (err) {
        updateSlot(esPersonId, { status: 'error', error: err as Error });
      }
    },
    [updateSlot],
  );

  const processNext = useCallback(async () => {
    const item = queueRef.current.shift();
    if (!item) return;

    if (item.outreach_type === 'whatsapp') {
      updateSlot(item.es_person_id, {
        status: 'success',
        data: { message: item.whatsappTemplateMessage ?? '' },
        error: null,
      });
      await processNext();
      return;
    }

    updateSlot(item.es_person_id, { status: 'loading' });

    try {
      const { data } = await generateAiReachoutMessageClient.generateReachoutMessageFromIds({
        es_person_id: item.es_person_id,
        job_improved_id: item.job_improved_id,
        outreach_type: item.outreach_type,
        language: item.language ?? 'nl',
        origin: OutreachOrigin.rjCommunicationBulk,
      });
      updateSlot(item.es_person_id, { status: 'success', data });
    } catch (err) {
      updateSlot(item.es_person_id, { status: 'error', error: err as Error });
    }

    await processNext();
  }, [updateSlot]);

  const generate = useCallback(
    (items: BulkGenerateItem[]) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;

      const initial = new Map<string, BulkSlot>();
      for (const item of items) {
        initial.set(item.es_person_id, {
          esPersonId: item.es_person_id,
          candidateName: item.candidateName ?? '',
          status: 'pending',
          data: null,
          error: null,
        });
      }
      setSlots(initial);

      queueRef.current = [...items];
      const workerCount = Math.min(concurrency, items.length);
      for (let i = 0; i < workerCount; i++) {
        processNext();
      }
    },
    [concurrency, processNext],
  );

  const removeSlot = useCallback((esPersonId: string) => {
    queueRef.current = queueRef.current.filter((item) => item.es_person_id !== esPersonId);
    setSlots((prev) => {
      const next = new Map(prev);
      next.delete(esPersonId);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    queueRef.current = [];
    isRunningRef.current = false;
    setSlots(EMPTY_MAP);
  }, []);

  const progress = useMemo<BulkProgress>(() => {
    const values = Array.from(slots.values());
    return {
      total: values.length,
      completed: values.filter((s) => s.status === 'success').length,
      failed: values.filter((s) => s.status === 'error').length,
      pending: values.filter((s) => s.status === 'pending' || s.status === 'loading').length,
    };
  }, [slots]);

  const isGenerating = progress.pending > 0;
  const isComplete = progress.total > 0 && progress.pending === 0 && progress.completed > 0;

  return { slots, progress, isGenerating, isComplete, generate, regenerateSlot, removeSlot, reset };
}
